/**
 * generateInvoicePdf — shared invoice PDF generator.
 *
 * Used by both the customer Order History (CustomerDashboard) and the admin
 * Orders Management (OrdersTab). The render is identical in both contexts so
 * the output is the document of record regardless of who downloads it.
 *
 * Side effects: triggers a file download via jsPDF's `doc.save(...)`.
 */
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function generateInvoicePdf(order) {
  // Fetch company info (issuer details)
  const companyResponse = await axios.get(`${API}/company-info`);
  const companyInfo = companyResponse.data;

  // Calculate totals — include adjustments as line items
  const allItems = [...(order.items || [])];
  if (order.adjustments && order.adjustments.length > 0) {
    order.adjustments.forEach((adj) => {
      const qty = parseInt(adj.quantity) || 1;
      allItems.push({
        product_name: adj.description,
        quantity: qty,
        price_per_unit: adj.unit_amount != null
          ? (adj.amount < 0 ? -Math.abs(adj.unit_amount) : Math.abs(adj.unit_amount))
          : adj.amount / qty,
        is_adjustment: true,
      });
    });
  }

  const subtotal = allItems.reduce(
    (sum, item) => sum + item.quantity * item.price_per_unit,
    0
  );
  const tax = subtotal * 0.1;

  const doc = new jsPDF();
  doc.setFont("helvetica");

  let yPos = 20;

  // Company Info (Left) and INVOICE (Right)
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(companyInfo.company_name || "Company Name", 20, yPos);

  doc.setFontSize(22);
  doc.text("INVOICE", 150, yPos);

  yPos += 10;

  // Company details
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const addressLines = (companyInfo.address || "").split("\n");
  addressLines.forEach((line) => {
    doc.text(line, 20, yPos);
    yPos += 5;
  });

  doc.text(`Phone: ${companyInfo.phone || "N/A"}`, 20, yPos);
  yPos += 5;
  doc.text(`Email: ${companyInfo.email || "N/A"}`, 20, yPos);
  yPos += 5;
  doc.text(`ABN: ${companyInfo.abn || "N/A"}`, 20, yPos);

  // Invoice details (Right side)
  let rightYPos = 30;
  doc.setFontSize(10);
  doc.text(`Invoice #: ${order.order_number || order.id.slice(0, 8)}`, 150, rightYPos);
  rightYPos += 6;
  doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, 150, rightYPos);
  rightYPos += 6;
  doc.text(`Delivery: ${new Date(order.delivery_date).toLocaleDateString()}`, 150, rightYPos);

  yPos += 15;

  // Bill To section
  doc.setFillColor(245, 245, 245);
  doc.rect(20, yPos, 170, 20, "F");
  yPos += 7;

  doc.setFont("helvetica", "bold");
  doc.text("Bill To:", 25, yPos);
  yPos += 6;

  doc.setFont("helvetica", "normal");
  doc.text(order.hotel_name || order.username || "", 25, yPos);
  yPos += 6;

  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Customer Code: ${order.hotel_code || order.username || ""}`, 25, yPos);
  doc.setTextColor(0);
  doc.setFontSize(10);

  yPos += 15;

  // Items table
  const tableData = allItems.map((item) => {
    const amount = item.quantity * item.price_per_unit;
    return [
      item.product_name,
      item.quantity.toString(),
      `$${item.price_per_unit.toFixed(2)}`,
      `$${amount.toFixed(2)}`,
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [["Item", "Qty", "Unit Price", "Amount"]],
    body: tableData,
    theme: "grid",
    headStyles: {
      fillColor: [220, 220, 220],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: 85 },
      1: { halign: "center", cellWidth: 25 },
      2: { halign: "right", cellWidth: 30 },
      3: { halign: "right", cellWidth: 30 },
    },
    margin: { left: 20, right: 20 },
    tableWidth: 170,
  });

  yPos = doc.lastAutoTable.finalY + 15;

  const table = doc.lastAutoTable;
  const tableStartX = table.settings.margin.left;
  const tableWidth = table.settings.tableWidth;
  const tableEndX = tableStartX + tableWidth;
  const amountColumnRight = tableEndX;
  const totalsLabelX = amountColumnRight - 60;
  const lineStartX = totalsLabelX;
  const lineEndX = amountColumnRight;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setDrawColor(220, 220, 220);

  // Subtotal
  doc.text("Subtotal:", totalsLabelX, yPos);
  doc.text(`$${subtotal.toFixed(2)}`, amountColumnRight, yPos, { align: "right" });
  doc.line(lineStartX, yPos + 2, lineEndX, yPos + 2);
  yPos += 8;

  // GST
  doc.text("GST (10%):", totalsLabelX, yPos);
  doc.text(`$${tax.toFixed(2)}`, amountColumnRight, yPos, { align: "right" });
  doc.line(lineStartX, yPos + 2, lineEndX, yPos + 2);
  yPos += 10;

  // Total
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.8);
  doc.line(lineStartX, yPos - 3, lineEndX, yPos - 3);

  doc.text("Total:", totalsLabelX, yPos + 2);
  doc.text(`$${order.total_price.toFixed(2)}`, amountColumnRight, yPos + 2, {
    align: "right",
  });

  doc.line(lineStartX, yPos + 6, lineEndX, yPos + 6);
  doc.line(lineStartX, yPos + 7, lineEndX, yPos + 7);
  doc.setLineWidth(0.1);
  yPos += 12;

  yPos += 15;

  if (order.notes) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Notes:", 20, yPos);
    yPos += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const notesLines = doc.splitTextToSize(order.notes, 170);
    doc.text(notesLines, 20, yPos);
    yPos += notesLines.length * 5 + 10;
  }

  if (companyInfo.notes) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Notes:", 20, yPos);
    yPos += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const companyNotesLines = doc.splitTextToSize(companyInfo.notes, 170);
    doc.text(companyNotesLines, 20, yPos);
    yPos += companyNotesLines.length * 5 + 10;
  }

  // Footer
  yPos = Math.max(yPos, 260);
  doc.setDrawColor(200);
  doc.line(20, yPos, 190, yPos);
  yPos += 8;

  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("Thank you for your business!", 105, yPos, { align: "center" });

  if (companyInfo.website) {
    yPos += 5;
    doc.text(companyInfo.website, 105, yPos, { align: "center" });
  }

  doc.save(`Invoice-${order.order_number || order.id.slice(0, 8)}.pdf`);
}
